import { useMemo, useRef, useState } from "react";
import { Copy, Download, Check } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  ok: { stroke: "#33456b", text: "#33456b" },
  warn: { stroke: "#b45309", text: "#b45309" },
  fail: { stroke: "#be123c", text: "#be123c" },
};

const LANE = 132;
const ROW = 46;
const HEAD = 44;
const TIME_W = 62;

export const SipLadder = ({ ladder, title = "call-flow-ladder" }) => {
  const [hover, setHover] = useState(-1);
  const [copied, setCopied] = useState(false);
  const svgRef = useRef(null);
  const actors = (ladder?.actors || []).filter(Boolean).slice(0, 6);
  const hops = useMemo(() => (ladder?.hops || []).filter((h) => h && (h.from || h.to)), [ladder]);
  if (actors.length < 2 || hops.length === 0) return null;

  const index = new Map(actors.map((a, i) => [String(a).toLowerCase(), i]));
  const at = (v, fallback) => {
    const i = index.get(String(v || "").toLowerCase());
    return i === undefined ? fallback : i;
  };
  const width = TIME_W + LANE * actors.length;
  const height = HEAD + ROW * hops.length + 14;
  const x = (i) => TIME_W + LANE * i + LANE / 2;

  const toPngBlob = async () => {
    const svg = svgRef.current;
    const xml = new XMLSerializer().serializeToString(svg);
    const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(xml)}`;
    const img = new Image();
    await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = url; });
    const scale = 2;
    const canvas = document.createElement("canvas");
    canvas.width = width * scale;
    canvas.height = height * scale;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.scale(scale, scale);
    ctx.drawImage(img, 0, 0);
    return new Promise((res) => canvas.toBlob(res, "image/png"));
  };

  const copyImage = async () => {
    try {
      const blob = await toPngBlob();
      await navigator.clipboard.write([new window.ClipboardItem({ "image/png": blob })]);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
      toast.success("Ladder copied — paste it straight into your carrier ticket");
    } catch {
      await downloadPng();
      toast.info("Your browser blocked the clipboard — downloaded the PNG instead");
    }
  };

  const downloadPng = async () => {
    try {
      const blob = await toPngBlob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${String(title).replace(/[^A-Za-z0-9_-]+/g, "-").slice(0, 60)}-ladder.png`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("PNG downloaded");
    } catch {
      toast.error("Could not render the PNG");
    }
  };

  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white" data-testid="sip-ladder">
      <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 bg-slate-50 px-3 py-1.5 text-[11px] text-slate-400">
        <span>Signalling path in time order</span>
        <span className="flex items-center gap-1"><i className="inline-block h-0.5 w-4" style={{ background: STATUS.warn.stroke }} /> degraded</span>
        <span className="flex items-center gap-1"><i className="inline-block h-0.5 w-4" style={{ background: STATUS.fail.stroke }} /> failing hop</span>
        <div className="ml-auto flex items-center gap-1.5">
          <button type="button" onClick={copyImage} data-testid="sip-ladder-copy"
            className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-600 transition-colors hover:border-primary/40 hover:text-primary">
            {copied ? <Check className="h-3 w-3 text-emerald-600" /> : <Copy className="h-3 w-3" />}
            {copied ? "Copied" : "Copy image"}
          </button>
          <button type="button" onClick={downloadPng} data-testid="sip-ladder-download"
            className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-600 transition-colors hover:border-primary/40 hover:text-primary">
            <Download className="h-3 w-3" /> PNG
          </button>
        </div>
      </div>
      <svg ref={svgRef} xmlns="http://www.w3.org/2000/svg" width={width} height={height} role="img" aria-label="Call flow ladder diagram" className="min-w-full">
        {actors.map((a, i) => (
          <g key={`actor-${i}`}>
            <rect x={TIME_W + LANE * i + 4} y={8} width={LANE - 8} height={24} rx={6}
              fill="#eef2f7" stroke="#e2e8f0" />
            <text x={x(i)} y={24} textAnchor="middle" fontSize="10.5" fontWeight="600" fill="#334155">
              {String(a).length > 18 ? `${String(a).slice(0, 17)}…` : a}
            </text>
            <line x1={x(i)} y1={36} x2={x(i)} y2={height - 6} stroke="#dbe3ec" strokeWidth="1" strokeDasharray="3 3" />
          </g>
        ))}
        {hops.map((h, n) => {
          const y = HEAD + ROW * n + 14;
          const tone = STATUS[String(h.status || "ok").toLowerCase()] || STATUS.ok;
          const a = at(h.from, 0);
          const b = at(h.to, Math.min(a + 1, actors.length - 1));
          const isSelf = a === b;
          const x1 = x(a);
          const x2 = isSelf ? x1 + 46 : x(b);
          const dir = x2 >= x1 ? 1 : -1;
          const failing = String(h.status || "").toLowerCase() === "fail";
          return (
            <g key={`hop-${n}`} onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(-1)}
              data-testid={`sip-ladder-hop-${n}`} style={{ cursor: "default" }}>
              <rect x={0} y={y - ROW / 2 + 4} width={width} height={ROW - 4}
                fill={hover === n ? "#f8fafc" : "transparent"} />
              <text x={6} y={y + 3} fontSize="9" fontFamily="ui-monospace, monospace" fill="#64748b">{h.time || ""}</text>
              {isSelf ? (
                <path d={`M ${x1} ${y} H ${x1 + 40} V ${y + 12} H ${x1 + 6}`} fill="none"
                  stroke={tone.stroke} strokeWidth={failing ? 2 : 1.3} />
              ) : (
                <line x1={x1} y1={y} x2={x2 - dir * 7} y2={y} stroke={tone.stroke} strokeWidth={failing ? 2 : 1.3} />
              )}
              <polygon
                points={isSelf
                  ? `${x1 + 6},${y + 12} ${x1 + 13},${y + 9} ${x1 + 13},${y + 15}`
                  : `${x2},${y} ${x2 - dir * 8},${y - 3.4} ${x2 - dir * 8},${y + 3.4}`}
                fill={tone.stroke} />
              <text x={isSelf ? x1 + 50 : (x1 + x2) / 2} y={y - 6} textAnchor={isSelf ? "start" : "middle"}
                fontSize="10" fontWeight="700" fill={tone.text}>
                {String(h.label || "").slice(0, 34)}
              </text>
              {h.note && (
                <text x={isSelf ? x1 + 50 : (x1 + x2) / 2} y={y + (isSelf ? 26 : 14)} textAnchor={isSelf ? "start" : "middle"}
                  fontSize="9" fill="#64748b">
                  {String(h.note).slice(0, 56)}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
};

export default SipLadder;
